var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Material } from "@babylonjs/core/Materials/material";
import { ShaderLanguage } from "@babylonjs/core/Materials/shaderLanguage";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Color4 } from "@babylonjs/core/Maths/math.color";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { serialize, serializeAsColor3 } from "@babylonjs/core/Misc/decorators";
import { SerializationHelper } from "@babylonjs/core/Misc/decorators.serialization";
import { RegisterClass } from "@babylonjs/core/Misc/typeStore";
import { MmdPluginMaterialSphereTextureBlendMode } from "./mmdPluginMaterial";
class MmdPluginMaterialMock {
    isMock = true;
    sphereTexture = null;
    sphereTextureBlendMode = MmdPluginMaterialSphereTextureBlendMode.Add;
    toonTexture = null;
    ignoreDiffuseWhenToonTextureIsNull = false;
    applyAmbientColorToDiffuse = true;
    clampAlpha = true;
    textureMultiplicativeColor = new Color4(1, 1, 1, 1);
    textureAdditiveColor = new Color4(0, 0, 0, 0);
    sphereTextureMultiplicativeColor = new Color4(1, 1, 1, 1);
    sphereTextureAdditiveColor = new Color4(0, 0, 0, 0);
    toonTextureMultiplicativeColor = new Color4(1, 1, 1, 1);
    toonTextureAdditiveColor = new Color4(0, 0, 0, 0);
    useTextureColor = false;
    useSphereTextureColor = false;
    useToonTextureColor = false;
}
/**
 * MMD standard material
 *
 * This material is an extension of the `StandardMaterial` and includes features for mmd's shading specifications
 *
 * The user can decide whether to activate each mmd shading specification
 *
 * If you disable all features, it behaves the same as `StandardMaterial`
 */
export class MmdStandardMaterial extends StandardMaterial {
    _pluginMaterial;
    _renderOutline = false;
    /**
     * Outline width (default: 0.01)
     */
    outlineWidth = 0.01;
    /**
     * Outline color (default: (0, 0, 0))
     */
    outlineColor = new Color3(0, 0, 0);
    /**
     * Outline alpha (default: 1.0)
     */
    outlineAlpha = 1.0;
    _disposed = false;
    /**
     * Create a new MMD standard material
     * @param name Define the name of the material in the scene
     * @param scene The scene the material belongs to
     * @param forceGLSL Use the GLSL code generation for the shader (even on WebGPU). Default is false
     */
    constructor(name, scene, forceGLSL = false) {
        super(name, scene, forceGLSL);
        this.specularColor = new Color3(0, 0, 0);
        const pluginMaterialMock = this._pluginMaterial = new MmdPluginMaterialMock();
        pluginMaterialMock.ignoreDiffuseWhenToonTextureIsNull = true;
        this._initPluginShaderSourceAsync(this._shaderLanguage);
    }
    async _initPluginShaderSourceAsync(shaderLanguage) {
        const mmdPluginMaterial = shaderLanguage === ShaderLanguage.GLSL
            ? (await import("./Shaders/mmdStandard")).MmdPluginMaterial
            : (await import("./ShadersWGSL/mmdStandard")).MmdPluginMaterial;
        if (this._disposed) {
            return;
        }
        const pluginMaterialMock = this._pluginMaterial;
        const pluginMaterial = this._pluginMaterial = new mmdPluginMaterial(this);
        pluginMaterial.isEnabled = true;
        // todo: copy optimization
        pluginMaterial.sphereTexture = pluginMaterialMock.sphereTexture;
        pluginMaterial.sphereTextureBlendMode = pluginMaterialMock.sphereTextureBlendMode;
        pluginMaterial.toonTexture = pluginMaterialMock.toonTexture;
        pluginMaterial.ignoreDiffuseWhenToonTextureIsNull = pluginMaterialMock.ignoreDiffuseWhenToonTextureIsNull;
        pluginMaterial.applyAmbientColorToDiffuse = pluginMaterialMock.applyAmbientColorToDiffuse;
        pluginMaterial.clampAlpha = pluginMaterialMock.clampAlpha;
        pluginMaterial.textureMultiplicativeColor = pluginMaterialMock.textureMultiplicativeColor;
        pluginMaterial.textureAdditiveColor = pluginMaterialMock.textureAdditiveColor;
        pluginMaterial.sphereTextureMultiplicativeColor = pluginMaterialMock.sphereTextureMultiplicativeColor;
        pluginMaterial.sphereTextureAdditiveColor = pluginMaterialMock.sphereTextureAdditiveColor;
        pluginMaterial.toonTextureMultiplicativeColor = pluginMaterialMock.toonTextureMultiplicativeColor;
        pluginMaterial.toonTextureAdditiveColor = pluginMaterialMock.toonTextureAdditiveColor;
        pluginMaterial.useTextureColor = pluginMaterialMock.useTextureColor;
        pluginMaterial.useSphereTextureColor = pluginMaterialMock.useSphereTextureColor;
        pluginMaterial.useToonTextureColor = pluginMaterialMock.useToonTextureColor;
    }
    /**
     * Disposes the material
     * @param forceDisposeEffect specifies if effects should be forcefully disposed
     * @param forceDisposeTextures specifies if textures should be forcefully disposed
     */
    dispose(forceDisposeEffect, forceDisposeTextures) {
        super.dispose(forceDisposeEffect, forceDisposeTextures);
        this._disposed = true;
    }
    /**
     * Get if the submesh is ready to be used and all its information available.
     * Child classes can use it to update shaders
     * @param mesh defines the mesh to check
     * @param subMesh defines which submesh to check
     * @param useInstances specifies that instances should be used
     * @returns a boolean indicating that the submesh is ready or not
     */
    isReadyForSubMesh(mesh, subMesh, useInstances) {
        if (this._pluginMaterial.isMock) {
            return false;
        }
        return super.isReadyForSubMesh(mesh, subMesh, useInstances);
    }
    /**
     * Get or set sphere texture
     */
    get sphereTexture() {
        return this._pluginMaterial.sphereTexture;
    }
    set sphereTexture(value) {
        this._pluginMaterial.sphereTexture = value;
    }
    /**
     * Get or set sphere texture blend mode (default: MmdPluginMaterialSphereTextureBlendMode.Add)
     */
    get sphereTextureBlendMode() {
        return this._pluginMaterial.sphereTextureBlendMode;
    }
    set sphereTextureBlendMode(value) {
        this._pluginMaterial.sphereTextureBlendMode = value;
    }
    /**
     * Get or set toon texture
     */
    get toonTexture() {
        return this._pluginMaterial.toonTexture;
    }
    set toonTexture(value) {
        this._pluginMaterial.toonTexture = value;
    }
    /**
     * If toe on texture is not set, decide whether to treat it as if it had white toon texture applied (default: true)
     *
     * In general, in order to get a stylized rendering, it's better to do true
     *
     * and if you want a more realistic rendering, it's better to do false
     */
    get ignoreDiffuseWhenToonTextureIsNull() {
        return this._pluginMaterial.ignoreDiffuseWhenToonTextureIsNull;
    }
    set ignoreDiffuseWhenToonTextureIsNull(value) {
        this._pluginMaterial.ignoreDiffuseWhenToonTextureIsNull = value;
    }
    /**
     * Get or set whether to apply ambient color to diffuse color (default: true)
     *
     * In babylon.js, the ambient color is not affected by the light source, but in mmd, it is
     *
     * Therefore, if you want to get a more mmd-like rendering, it is better to do true
     */
    get applyAmbientColorToDiffuse() {
        return this._pluginMaterial.applyAmbientColorToDiffuse;
    }
    set applyAmbientColorToDiffuse(value) {
        this._pluginMaterial.applyAmbientColorToDiffuse = value;
    }
    /**
     * Get or set whether to apply clamp `Material.alpha` to 0.0 .. 1.0 (default: false)
     *
     * Babylon.js does not clamp the alpha value
     */
    get clampAlpha() {
        return this._pluginMaterial.clampAlpha;
    }
    set clampAlpha(value) {
        this._pluginMaterial.clampAlpha = value;
    }
    /**
     * Get or set whether to use the texture multiplicative color (default: (1, 1, 1, 1))
     *
     * If this property is first accessed as set, the shader is recompiled to support texture color properties
     *
     * After that, the feature is no longer turned off to prevent shader recompilation
     *
     * These features are subject to change at a later date
     */
    get textureMultiplicativeColor() {
        this._pluginMaterial.useTextureColor = true;
        return this._pluginMaterial.textureMultiplicativeColor;
    }
    set textureMultiplicativeColor(value) {
        this._pluginMaterial.useTextureColor = true;
        this._pluginMaterial.textureMultiplicativeColor = value;
    }
    /**
     * Get or set whether to use the texture additive color (default: (0, 0, 0, 0))
     *
     * If this property is first accessed as set, the shader is recompiled to support texture color properties
     *
     * After that, the feature is no longer turned off to prevent shader recompilation
     *
     * These features are subject to change at a later date
     */
    get textureAdditiveColor() {
        this._pluginMaterial.useTextureColor = true;
        return this._pluginMaterial.textureAdditiveColor;
    }
    set textureAdditiveColor(value) {
        this._pluginMaterial.useTextureColor = true;
        this._pluginMaterial.textureAdditiveColor = value;
    }
    /**
     * Get or set whether to use the sphere texture multiplicative color (default: (1, 1, 1, 1))
     *
     * If this property is first accessed as set, the shader is recompiled to support texture color properties
     *
     * After that, the feature is no longer turned off to prevent shader recompilation
     *
     * These features are subject to change at a later date
     */
    get sphereTextureMultiplicativeColor() {
        this._pluginMaterial.useSphereTextureColor = true;
        return this._pluginMaterial.sphereTextureMultiplicativeColor;
    }
    set sphereTextureMultiplicativeColor(value) {
        this._pluginMaterial.useSphereTextureColor = true;
        this._pluginMaterial.sphereTextureMultiplicativeColor = value;
    }
    /**
     * Get or set whether to use the sphere texture additive color (default: (0, 0, 0, 0))
     *
     * If this property is first accessed as set, the shader is recompiled to support texture color properties
     *
     * After that, the feature is no longer turned off to prevent shader recompilation
     *
     * These features are subject to change at a later date
     */
    get sphereTextureAdditiveColor() {
        this._pluginMaterial.useSphereTextureColor = true;
        return this._pluginMaterial.sphereTextureAdditiveColor;
    }
    set sphereTextureAdditiveColor(value) {
        this._pluginMaterial.useSphereTextureColor = true;
        this._pluginMaterial.sphereTextureAdditiveColor = value;
    }
    /**
     * Get or set whether to use the toon texture multiplicative color (default: (1, 1, 1, 1))
     *
     * If this property is first accessed as set, the shader is recompiled to support texture color properties
     *
     * After that, the feature is no longer turned off to prevent shader recompilation
     *
     * These features are subject to change at a later date
     */
    get toonTextureMultiplicativeColor() {
        this._pluginMaterial.useToonTextureColor = true;
        return this._pluginMaterial.toonTextureMultiplicativeColor;
    }
    set toonTextureMultiplicativeColor(value) {
        this._pluginMaterial.useToonTextureColor = true;
        this._pluginMaterial.toonTextureMultiplicativeColor = value;
    }
    /**
     * Get or set whether to use the toon texture additive color (default: (0, 0, 0, 0))
     *
     * If this property is first accessed as set, the shader is recompiled to support texture color properties
     *
     * After that, the feature is no longer turned off to prevent shader recompilation
     *
     * These features are subject to change at a later date
     */
    get toonTextureAdditiveColor() {
        this._pluginMaterial.useToonTextureColor = true;
        return this._pluginMaterial.toonTextureAdditiveColor;
    }
    set toonTextureAdditiveColor(value) {
        this._pluginMaterial.useToonTextureColor = true;
        this._pluginMaterial.toonTextureAdditiveColor = value;
    }
    /**
     * Whether to use the outline rendering (default: false)
     */
    get renderOutline() {
        return this._renderOutline;
    }
    set renderOutline(value) {
        // Lazy Load the component
        if (value) {
            this.getScene().getMmdOutlineRenderer?.();
        }
        this._renderOutline = value;
    }
    /**
     * Specifies if the material will require alpha blending
     * @returns a boolean specifying if alpha blending is needed
     */
    needAlphaBlending() {
        if (this._disableAlphaBlending) {
            return false;
        }
        return super.needAlphaBlending() ||
            (this._pluginMaterial.sphereTexture !== null &&
                this._pluginMaterial.sphereTextureBlendMode === MmdPluginMaterialSphereTextureBlendMode.Multiply);
    }
    // NOTE: clone method is just a copy of the original method with a different return type. becareful to babylon.js internal changes
    /**
     * Makes a duplicate of the material, and gives it a new name
     * @param name defines the new name for the duplicated material
     * @param cloneTexturesOnlyOnce - if a texture is used in more than one channel (e.g diffuse and opacity), only clone it once and reuse it on the other channels. Default false.
     * @param rootUrl defines the root URL to use to load textures
     * @returns the cloned material
     */
    clone(name, cloneTexturesOnlyOnce = true, rootUrl = "") {
        const result = SerializationHelper.Clone(() => new MmdStandardMaterial(name, this.getScene()), this, { cloneTexturesOnlyOnce });
        result.name = name;
        result.id = name;
        this.stencil.copyTo(result.stencil);
        this._clonePlugins(result, rootUrl);
        return result;
    }
    // NOTE: Parse method is just a copy of the original method with a different return type. becareful to babylon.js internal changes
    /**
     * Creates a mmd standard material from parsed material data
     * @param source defines the JSON representation of the material
     * @param scene defines the hosting scene
     * @param rootUrl defines the root URL to use to load textures and relative dependencies
     * @returns a new standard material
     */
    static Parse(source, scene, rootUrl) {
        const material = SerializationHelper.Parse(() => new MmdStandardMaterial(source.name, scene), source, scene, rootUrl);
        if (source.stencil) {
            material.stencil.parse(source.stencil, scene, rootUrl);
        }
        Material._ParsePlugins(source, material, scene, rootUrl);
        return material;
    }
}
__decorate([
    serialize("renderOutline")
], MmdStandardMaterial.prototype, "_renderOutline", void 0);
__decorate([
    serialize()
], MmdStandardMaterial.prototype, "outlineWidth", void 0);
__decorate([
    serializeAsColor3()
], MmdStandardMaterial.prototype, "outlineColor", void 0);
__decorate([
    serialize()
], MmdStandardMaterial.prototype, "outlineAlpha", void 0);
RegisterClass("BABYLON.MmdStandardMaterial", MmdStandardMaterial);
